<template>
  <v-card
    flat
    outlined
    rounded="lg"
    color="cardbg"
    height="100%"
    class="flex"
    elevation="10"
  >
    <v-card-title
      color="primary"
      :style="`
        padding: calc(${scale} * 0.4rem);
        line-height: calc(${scale} * 0.75rem)
      `"
      class="font-weight-medium titlecolor--text d-flex justify-space-between"
    >
      <div :style="`font-size: calc(${scale} * 0.7rem);
       display: flex; align-items: center; gap: 8px;`">
        <span
          v-if="title && title.includes('Live')"
          :class="['live-title', { 'live-title--empty': noData }]"
        >
          {{ title }}
        </span>
        <span v-else>{{ title }}</span>
      </div>

      <span class="subtitlecolor--text" :style="`font-size: calc(${scale} * 0.7rem);`">
        {{ daterange }}
      </span>
    </v-card-title>
    <v-card-text style="height: 92%; padding: 0; padding-left: 8px; line-height: normal;">
      <WidgetLoading v-if="loading" />
      <div
        v-else-if="noData"
        class="subtitlecolor--text d-flex align-center justify-center"
        style="height: 100%; width: 100%;"
        :style="`font-size: calc(${scale} * 0.75rem);`"
      >
        No Data Available
      </div>
      <v-window v-model="onboarding" style="height: 100%;" v-else>
        <v-window-item
          style="height: 100%;"
          :key="`winitem-${i}`"
          v-for="(chartoption, i) in chartOptions"
        >
          <div
            class="d-flex"
            style="align-items: center;"
            v-for="(chart, i) in chartoption"
            :key="i"
            :style="`height: ${chartHeight}%;`"
          >
            <span
              class="subtitlecolor--text"
              :style="`font-size: calc(${scale} * 0.7rem); width: calc(${scale} * 3rem)`"
            >
              {{ chart.machinename }}
            </span>
            <highcharts
              :options="chart.options"
              style="height: 100%; width: 100%;"
              :key="`${uuid}${i}`"
            ></highcharts>
          </div>
        </v-window-item>
      </v-window>
    </v-card-text>
    <v-card-actions
      v-if="!hideSlideControls"
      class="justify-space-between pa-0"
      style="height: 2%;"
    >
      <v-btn
        text
        @click="prev"
        class="pa-0"
        :style="`min-width: 0; height: calc(${scale} * 20px)`"
      >
        <v-icon :size="scale * 16">mdi-chevron-left</v-icon>
      </v-btn>
      <v-item-group
        v-model="onboarding"
        class="text-center"
        mandatory
      >
        <v-item
          v-for="n in chartOptions.length"
          :key="`btn-${n}`"
          v-slot="{ active, toggle }"
        >
          <v-btn
            :input-value="active"
            icon
            @click="toggle"
            class="pa-0"
            :style="`min-width: 0; height: calc(${scale} * 18px); width: calc(${scale} * 18px)`"
          >
            <v-icon :size="scale * 18">mdi-record</v-icon>
          </v-btn>
        </v-item>
      </v-item-group>
      <v-btn
        text
        @click="next"
        class="pa-0"
        style="min-width: 0; height: 24px;"
      >
        <v-icon :size="scale * 16">mdi-chevron-right</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import WidgetLoading from './WidgetLoading.vue';

export default {
  name: 'MachinewiseChart',
  props: {
    title: {
      type: String,
    },
    daterange: {
      type: String,
    },
    scale: {
      type: Number,
      default: 1,
    },
    unit: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    WidgetLoading,
  },
  data() {
    return {
      onboarding: 0,
      uuid: 'uuid',
      chartOptions: [],
      chartHeight: 9,
      timeInterval: null,
    };
  },
  computed: {
    ...mapState('cellwiseDowntimeDashboard', ['config', 'theme']),
    ...mapState('helper', ['isFullscreen']),
    activeTheme() {
      return (this.theme && this.theme.isDark) ? 'dark' : 'light';
    },
    hideSlideControls() {
      return this.isFullscreen;
    },
    slidetime() {
      const seconds = (this.config && this.config.last7DaysWidgetSlideTimeSec) || 10;
      return seconds * 1000;
    },
    noData() {
      return !this.chartOptions || this.chartOptions.length <= 0;
    },
  },
  watch: {
    onboarding() {
      this.$nextTick(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 350);
      });
    },
    scale(val) {
      this.adjustSize(val);
      this.createUUID();
      this.$nextTick(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 350);
      });
    },
    activeTheme() {
      this.adjustSize(this.scale);
      this.createUUID();
    },
    slidetime() {
      this.slideInterval();
    },
  },
  mounted() {
    this.slideInterval();
  },
  beforeDestroy() {
    clearInterval(this.timeInterval);
  },
  methods: {
    assignData(result) {
      if (result.length > 0 && result[0].length > 0) {
        this.chartHeight = 100 / result[0].length;
      } else {
        this.chartHeight = 9;
      }
      this.chartOptions = result;
      this.onboarding = 0;
      this.adjustSize(this.scale);
      this.createUUID();
      this.$nextTick(() => {
        this.slideInterval();
      });
    },
    adjustSize(scale) {
      if (this.chartOptions.length > 0) {
        const lablesize = 8 * scale;
        const datalablesize = 7 * scale;
        const padding = 15 * scale;

        const isDark = this.$vuetify.theme.dark;

        const textColor = isDark ? '#FFFFFF' : '#333333';
        const gridColor = isDark ? '#495767' : '#E0E0E0';

        const xlabels = {
          enabled: true,
          reserveSpace: true,
          y: padding,
          style: {
            color: textColor,
            fontSize: `${lablesize}px`,
            fontWeight: 'normal',
            fontFamily: 'Poppins, "Open Sans", sans-serif',
          },
        };
        const ylables = {
          style: {
            color: textColor,
            fontSize: `${lablesize}px`,
            fontWeight: 'normal',
            fontFamily: 'Poppins, "Open Sans", sans-serif',
          },
        };

        this.chartOptions.forEach((chartoption, i) => {
          const newOptions = [...chartoption];
          chartoption.forEach((chart, j) => {
            newOptions[j].options.xAxis.labels = xlabels;
            newOptions[j].options.yAxis.labels = ylables;
            newOptions[j].options.yAxis.gridLineColor = gridColor;
            newOptions[j].options.plotOptions.series.dataLabels = {
              enabled: true,
              style: {
                fontSize: `${datalablesize}px`,
                color: '#FFFFFF',
                textOutline: '1px contrast',
                fontWeight: 'normal',
                fontFamily: 'Poppins, "Open Sans", sans-serif',
              },
              crop: false,
              overflow: 'allow',
            };
          });
          this.chartOptions[i] = newOptions;
        });
      }
    },
    slideInterval() {
      clearInterval(this.timeInterval);
      if (!this.chartOptions || this.chartOptions.length <= 1) {
        this.timeInterval = null;
        return;
      }
      this.timeInterval = setInterval(() => {
        this.next();
      }, this.slidetime);
    },
    next() {
      if (this.chartOptions.length <= 0) return;
      this.onboarding = this.onboarding + 1 === this.chartOptions.length
        ? 0
        : this.onboarding + 1;
    },
    prev() {
      if (this.chartOptions.length <= 0) return;
      this.onboarding = this.onboarding - 1 < 0
        ? this.chartOptions.length - 1
        : this.onboarding - 1;
    },
    createUUID() {
      /* eslint-disable */
      var dt = new Date().getTime();
      var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = (dt + Math.random()*16)%16 | 0;
        dt = Math.floor(dt/16);
        return (c=='x' ? r :(r&0x3|0x8)).toString(16);
      });
      this.uuid =  uuid;
    },
  },
};
</script>
<style lang="scss" scoped>
.live-title {
  display: inline-block;
  color: #00E676;
  font-weight: 700;
  line-height: 1;
}

.live-title--empty {
  color: #FF5252;
  text-shadow: 0 0 8px #FF5252;
}
</style>
<style lang="scss" scoped>
.w-full {
  width: 100%;
}
.h-full {
  height: 100%;
}
</style>
